import { Fragment, ChangeEvent, useContext, useState } from 'react';
import { gql, useLazyQuery, useMutation } from '@apollo/client';
import { debounce } from 'debounce';
import {
  DevHubAutocomplete,
  DevHubBox,
  DevHubButton,
  DevHubCircularProgress,
  DevHubDynamicIcon,
  DevHubModal,
  DevHubTextField,
} from '@devxdiscover/devhub-ui';
import { SystemContext } from '../../contexts/SystemContext';
import { Tag, TagInput } from './types/TagTypes';

const TagQuery = gql`
  query Tags($filter: ResourceCatalogTagFilter) {
    resources {
      tags(filter: $filter) {
        id
        tag
      }
    }
  }
`;

const AddTagMutation = gql`
  mutation AddTag($input: AddTagInput!) {
    resources {
      tags {
        addTag(input: $input) {
          success
        }
      }
    }
  }
`;

type Props = {
  applicationId: string;
};

/**
 * AddTag uses a fusion mutation to add a tag to the database
 * @param props props of the application
 * @param props.applicationId the id of the application being edited
 */
const AddTag = (props: Props) => {
  const [open, setOpen] = useState<boolean>(false);
  const [tags, setTags] = useState<Tag[]>([]);
  const [selectedTag, setSelectedTag] = useState<Tag>({} as Tag);
  const { GetApplicationQuery, applicationVariables } =
    useContext(SystemContext);

  const [getTags, { loading: queryLoading, error: queryError }] = useLazyQuery(
    TagQuery,
    {
      errorPolicy: 'all',
    },
  );

  const [addTag, { loading: mutationLoading }] = useMutation(AddTagMutation, {
    refetchQueries: [
      { query: GetApplicationQuery, variables: applicationVariables },
      'GetApplicationDetails',
    ],
  });

  const handleSearch = debounce((value: string) => {
    if (value) {
      getTags({
        variables: { filter: { tag: `*${value}*` } },
      }).then(tagResults => {
        if (tagResults?.data?.resources?.tags?.length > 0) {
          setTags(tagResults?.data?.resources?.tags);
        }
      });
    }
  });

  const onSelect = (_: any, value: any | null) => {
    if (!value) {
      return;
    }
    setSelectedTag(value);
  };

  const handleOpenModal = () => {
    setOpen(true);
  };

  const handleCloseModal = () => {
    setOpen(false);
  };

  const handleSubmit = () => {
    if (selectedTag) {
      const input: TagInput = {
        applicationId: Number(props?.applicationId),
        tagId: Number(selectedTag?.id),
      };

      addTag({ variables: { input: input } });
    }
    handleCloseModal();
  };

  // Event Handler for the text box and to trigger the query after 3 keystrokes
  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    if (value.length >= 3) {
      handleSearch(value);
    }
  };

  return (
    <Fragment>
      <DevHubButton
        variant="contained"
        color="primary"
        height="large"
        startIcon={
          mutationLoading ? (
            <DevHubCircularProgress color="inherit" thickness={6} size={22} />
          ) : (
            <DevHubDynamicIcon iconName="Add" />
          )
        }
        onClick={handleOpenModal}
      >
        Add Tag
      </DevHubButton>
      <DevHubModal open={open} onClose={handleCloseModal}>
        <Fragment>
          {queryError ? (
            <DevHubTextField
              error
              label="Error"
              defaultValue="No tags Available"
              helperText="Failed to fetch tag details."
              variant="outlined"
            />
          ) : (
            <DevHubAutocomplete
              loading={queryLoading}
              loadingText="Loading.."
              options={tags}
              onChange={onSelect}
              getOptionLabel={option => option?.tag}
              renderInput={params => (
                <DevHubTextField
                  {...params}
                  label="Search for a tag"
                  margin="normal"
                  helperText="Tag you wish to add to the application"
                  variant="outlined"
                  required
                  onChange={onChange}
                  InputProps={params.InputProps}
                />
              )}
            />
          )}
          <DevHubBox display="flex" justifyContent="space-between">
            <DevHubButton
              variant="contained"
              color="primary"
              onClick={handleCloseModal}
            >
              Cancel
            </DevHubButton>
            <DevHubButton
              variant="contained"
              color="primary"
              onClick={handleSubmit}
            >
              Submit
            </DevHubButton>
          </DevHubBox>
        </Fragment>
      </DevHubModal>
    </Fragment>
  );
};

export default AddTag;
